import copy from "copy-to-clipboard";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DropDown } from "@normasteaching/norma-ui";
import { Ellipsis } from "lucide-react";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import { useMessageController } from "../../UI/useMessageController.ts";
import {
  CouponType,
  NormasTeachingCouponDTO,
} from "../../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { useSetNormasTeachingCouponStatus } from "../../ProductService/NormasTeachingCoupon/useSetNormasTeachingCouponStatus.ts";
import { useNavigate } from "react-router";
import { useState } from "react";
import CouponDetailsModal from "./NormasTeachingCouponDetailModal.tsx";
import { ActiveBadge } from "../../UI/Admin/ActiveBadge.tsx";
import {
  CreateNormasTeachingCouponScreenPath,
  DuplicateCouponSearchParam,
} from "./CreateNormasTeachingCouponScreen.tsx";

function getCouponStatus(coupon: NormasTeachingCouponDTO): boolean {
  const now = new Date();
  if (!coupon.isActive) return false;
  else if (coupon.validFrom && now < new Date(coupon.validFrom)) return false;
  else return !(coupon.validTo && now > new Date(coupon.validTo));
}

const columnHelper = createColumnHelper<NormasTeachingCouponDTO>();
const columns = [
  columnHelper.accessor("couponCode", {
    header: "Coupon Code",
    cell: function IdColumn(info) {
      const { showMessage, message, open } = useMessageController();
      return (
        <>
          <SuccessMessage open={open} message={message} />
          <span
            className={"cursor-pointer"}
            onClick={() => {
              copy(info.getValue());
              showMessage({
                message: "Codice del coupon copiato negli appunti!",
                timeoutMs: 4 * 1000,
              });
            }}
          >
            {info.getValue().toUpperCase()}
          </span>
        </>
      );
    },
    maxSize: 100,
  }),
  columnHelper.accessor("value", {
    header: "Value",
    cell: (info) => {
      const v = info.getValue() ?? 0;
      return `${v.couponType === CouponType.Fixed ? `${v.amount / 100}€` : `${v.amount}%`}`;
    },
    maxSize: 100,
  }),
  columnHelper.accessor("validFrom", {
    header: "Valid From",
    cell: (info) => {
      const v = info.getValue();
      return `${
        v
          ? `${new Date(v).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}`
          : "-"
      }`;
    },
    maxSize: 100,
  }),
  columnHelper.accessor("validTo", {
    header: "Valid To",
    cell: (info) => {
      const v = info.getValue();
      return `${
        v
          ? `${new Date(v).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}`
          : "-"
      }`;
    },
    maxSize: 100,
  }),
  columnHelper.accessor("isActive", {
    header: "Status",
    cell: (info) => <ActiveBadge active={getCouponStatus(info.row.original)} />,
    maxSize: 100,
  }),
  columnHelper.display({
    id: "actions",
    cell: function Actions(props) {
      const { showMessage, open, message } = useMessageController();
      const navigate = useNavigate();
      const [showDetails, setShowDetails] = useState(false);

      const { setNormasTeachingCouponStatus } =
        useSetNormasTeachingCouponStatus();

      return (
        <>
          <SuccessMessage open={open} message={message} />
          {showDetails && (
            <CouponDetailsModal
              coupon={props.row.original}
              onClose={() => setShowDetails(false)}
            />
          )}
          <DropDown activator={<Ellipsis className={"w-5"} />}>
            <div className={""}>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => setShowDetails(true)}
              >
                <span>Visualizza Dettagli</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() =>
                  navigate(
                    `/admin/normasteaching/edit-coupon/${props.row.original._id}`,
                  )
                }
              >
                <span>Modifica</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() =>
                  navigate(
                    `${CreateNormasTeachingCouponScreenPath}?${DuplicateCouponSearchParam}=${props.row.original._id}`,
                  )
                }
              >
                <span>Duplica</span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  setNormasTeachingCouponStatus({
                    couponId: props.row.original._id,
                    isActive: !props.row.original.isActive,
                  });
                  showMessage({
                    message: `Coupon ${props.row.original.couponCode} ${
                      props.row.original.isActive ? "Disattivato" : "Attivato"
                    } con successo!`,
                    timeoutMs: 4 * 1000,
                  });
                }}
              >
                <span>
                  {props.row.original.isActive ? "Disattiva" : "Attiva"}
                </span>
              </div>
              <div
                className={
                  "p-3 hover:bg-primary hover:text-white cursor-pointer"
                }
                onClick={() => {
                  copy(props.row.original.couponCode);
                  showMessage({
                    message: "Codice del coupon copiato negli appunti!",
                    timeoutMs: 4 * 1000,
                  });
                }}
              >
                <span>Copia Codice Coupon</span>
              </div>
            </div>
          </DropDown>
        </>
      );
    },
  }),
];
export function AdminNormasTeachingCouponTable({
  coupons,
}: {
  coupons: NormasTeachingCouponDTO[];
}) {
  const [selectedCoupon, setSelectedCoupon] =
    useState<NormasTeachingCouponDTO | null>(null);

  const table = useReactTable({
    data: coupons,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {selectedCoupon && (
        <CouponDetailsModal
          coupon={selectedCoupon}
          onClose={() => setSelectedCoupon(null)}
        />
      )}
      <table
        className={
          "w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
        }
      >
        <thead
          className={
            "text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
          }
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className={""} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th className={"px-6 py-4"} key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              className={
                "bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
              }
              key={row.id}
              onClick={() => setSelectedCoupon(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  style={{
                    maxWidth: cell.column.columnDef.maxSize
                      ? cell.column.columnDef.maxSize
                      : "none",
                    overflow:
                      cell.column.id !== "actions" ? "hidden" : undefined,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  className={"px-6 py-4"}
                  key={cell.id}
                  onClick={
                    cell.column.id === "actions"
                      ? (e) => e.stopPropagation()
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
    </>
  );
}
